/**
 * DashboardLayout - Layout za member dashboard sa Dashboard dizajnom
 *
 * Modern layout sa glassmorphism sidebar-om, top bar-om i Dashboard styling-om.
 * Podržava collapse sidebar i mobile drawer.
 *
 * @example
 * ```tsx
 * <DashboardLayout>
 *   <YourContent />
 * </DashboardLayout>
 * ```
 */

"use client";

import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Spinner,
  useDisclosure,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useDashboardTheme } from "@/components/dashboard/DashboardThemeProvider";
import { StaffOnlineHeartbeat } from "@/features/dashboard/online/StaffOnlineHeartbeat";
import { ProductNoticeBanner } from "@/features/product-notices/ProductNoticeBanner";
import { TuitionCalculationWatcher } from "@/features/tuition/components/TuitionCalculationWatcher";
import {
  DashboardSidebarProvider,
  RoleSwitchProvider,
  TenantBrandingProvider,
  useRoleSwitch,
} from "@/lib/contexts";
import { useClassicDashboardStyles } from "@/lib/hooks/useClassicDashboardStyles";
import { getDashboardSidebarWidth } from "@/lib/layout/dashboard-sidebar";
import { useDashboardSidebarCollapsedState } from "@/lib/layout/useDashboardSidebarCollapsedState";
import { fullViewportHeightProps } from "@/lib/layout/viewport";
import { useThemeTokens } from "@/lib/theme/tokens";
import { ChoreographerAgreementGate } from "@/features/dances/ChoreographerAgreementGate";
import { ActiveRoleApiHeader } from "./ActiveRoleApiHeader";
import { DashboardSidebar } from "./DashboardSidebar";
import { DashboardTopBar } from "./DashboardTopBar";
import { Footer } from "./Footer";

interface DashboardLayoutProps {
  children: ReactNode;
  initialLogoUrl?: string | null;
  initialTenantName?: string | null;
  /** Current active role from server session; used to hide role-switch overlay after refresh. */
  initialActiveRole?: string | null;
}

function DashboardLayoutContent({
  children,
  initialLogoUrl,
  initialTenantName,
  isCollapsed,
  onToggleCollapse,
}: DashboardLayoutProps & {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const theme = useThemeTokens();
  const { effectiveDashboardAppearance } = useDashboardTheme();
  const { isClassic } = useClassicDashboardStyles();
  const isDark = theme.mode === "dark";
  const roleSwitch = useRoleSwitch();
  const isRoleSwitching = roleSwitch?.isSwitching ?? false;

  return (
    <Flex
      h="100vh"
      maxH="100vh"
      overflow="hidden"
      bg={theme.bg.primary}
      position="relative"
      data-dashboard-appearance={effectiveDashboardAppearance}
      data-testid="dashboard-layout-root"
      sx={{
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          bg: theme.scrollbar.track,
        },
        "&::-webkit-scrollbar-thumb": {
          bg: theme.scrollbar.thumb,
          borderRadius: "4px",
          "&:hover": {
            bg: theme.scrollbar.thumbHover,
          },
        },
      }}
    >
      <ActiveRoleApiHeader />
      <StaffOnlineHeartbeat />
      {/* Full-page loading overlay when switching role */}
      {isRoleSwitching && (
        <Flex
          position="fixed"
          inset={0}
          zIndex={9999}
          bg={isDark ? "blackAlpha.700" : "whiteAlpha.800"}
          backdropFilter="blur(6px)"
          alignItems="center"
          justifyContent="center"
          pointerEvents="auto"
        >
          <Flex direction="column" align="center" gap={4}>
            <Spinner
              size="lg"
              color={theme.accent.primary}
              thickness="3px"
              emptyColor={theme.borderGlass.default}
            />
            <Box fontSize="sm" fontWeight="500" color={theme.text.secondary}>
              Switching role...
            </Box>
          </Flex>
        </Flex>
      )}

      {/* Desktop Sidebar */}
      <Box
        as="aside"
        w={{ base: 0, md: `${getDashboardSidebarWidth(isCollapsed)}px` }}
        display={{ base: "none", md: "block" }}
        flexShrink={0}
        transition="width 0.2s ease"
        position="relative"
        zIndex={10}
      >
        <DashboardSidebar
          isCollapsed={isCollapsed}
          onCollapse={onToggleCollapse}
          initialLogoUrl={initialLogoUrl}
          initialTenantName={initialTenantName}
        />
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
        <DrawerOverlay
          bg={
            isClassic
              ? "rgba(11,13,16,0.45)"
              : isDark
                ? "rgba(0,0,0,0.8)"
                : "rgba(11,13,16,0.6)"
          }
          backdropFilter={isClassic ? "none" : "blur(8px)"}
        />
        <DrawerContent
          maxW={`${getDashboardSidebarWidth(false)}px`}
          {...fullViewportHeightProps}
          overflow="hidden"
          bg={
            isClassic
              ? theme.bg.sidebar
              : isDark
                ? "rgba(11,13,16,0.95)"
                : "rgba(255,255,255,0.95)"
          }
          borderRightWidth="1px"
          borderColor={theme.borderGlass.default}
          borderRadius="0"
        >
          <DashboardSidebar
            isMobile
            onClose={onClose}
            initialLogoUrl={initialLogoUrl}
            initialTenantName={initialTenantName}
          />
        </DrawerContent>
      </Drawer>

      {/* Main Content */}
      <Flex
        direction="column"
        flex="1"
        minW={0}
        overflow="hidden"
        position="relative"
        zIndex={1}
      >
        <Box
          flex="1"
          overflowY="auto"
          overflowX="hidden"
          position="relative"
          display="flex"
          flexDirection="column"
          data-scroll-container
          sx={{
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              bg: theme.scrollbar.track,
            },
            "&::-webkit-scrollbar-thumb": {
              bg: theme.scrollbar.thumb,
              borderRadius: "4px",
              "&:hover": {
                bg: theme.scrollbar.thumbHover,
              },
            },
          }}
        >
          <TuitionCalculationWatcher />
          <DashboardTopBar onMenuClick={onOpen} />
          <ProductNoticeBanner />
          <ChoreographerAgreementGate>
            <Box w="100%">{children}</Box>
            <Box w="100%" mt="auto">
              <Footer fullWidth />
            </Box>
          </ChoreographerAgreementGate>
        </Box>
      </Flex>
    </Flex>
  );
}

export const DashboardLayout = (props: DashboardLayoutProps) => (
  <RoleSwitchProvider initialActiveRole={props.initialActiveRole}>
    <TenantBrandingProvider initialLogoUrl={props.initialLogoUrl}>
      <DashboardLayoutWithSidebar {...props} />
    </TenantBrandingProvider>
  </RoleSwitchProvider>
);

function DashboardLayoutWithSidebar(props: DashboardLayoutProps) {
  const { isCollapsed, toggleCollapse } = useDashboardSidebarCollapsedState();

  return (
    <DashboardSidebarProvider isCollapsed={isCollapsed}>
      <DashboardLayoutContent
        {...props}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleCollapse}
      />
    </DashboardSidebarProvider>
  );
}
